import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  FilterSelect,
  JourneyPlanningTable,
  PageBody,
  buttonVariants,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import {
  JourneyActionDialogs,
  type JourneyAction,
} from '@/components/journeys/JourneyActionDialogs.tsx';
import { JourneysFeatureNotice } from '@/components/journeys/JourneysFeatureNotice.tsx';
import { ManagerReachabilityBadge } from '@/components/journeys/ManagerReachabilityBadge.tsx';
import {
  JOURNEY_FILTERS,
  isJourneyFilter,
  matchesJourneyFilter,
  type JourneyFilter,
} from '@/components/journeys/journeyFilter.ts';
import {
  JOURNEY_LOCALE,
  JOURNEY_PLANNING_LABELS,
} from '@/components/journeys/journeyLabels.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { listJourneys } from '@/lib/journeys.ts';

/* In the URL, so coming back from a journey keeps the view. `all` is the one nobody asks for. */
export interface JourneysSearch {
  status?: Exclude<JourneyFilter, 'all'>;
}

export const Route = createFileRoute('/_app/journeys/')({
  staticData: { orgScope: 'required' },
  validateSearch: (search: Record<string, unknown>): JourneysSearch => ({
    status:
      isJourneyFilter(search.status) && search.status !== 'all'
        ? search.status
        : undefined,
  }),
  component: JourneysRoute,
});

function JourneysRoute() {
  const router = useRouter();

  return (
    <JourneysPage
      filter={Route.useSearch().status ?? 'all'}
      onFilterChange={(filter) =>
        void router.navigate({
          to: '/journeys',
          search: { status: filter === 'all' ? undefined : filter },
          replace: true,
        })
      }
    />
  );
}

export interface JourneysPageProps {
  filter: JourneyFilter;
  onFilterChange: (filter: JourneyFilter) => void;
}

/**
 * The active organisation's journeys, planning only. Filtered on "action
 * required" it is the overview of which managers are behind, which staff
 * follow up in person once the reminders have gone quiet.
 */
export function JourneysPage({ filter, onFilterChange }: JourneysPageProps) {
  const router = useRouter();
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id ?? null;

  const journeys = useQuery({
    queryKey: ['journeys', organisationId],
    queryFn: () => listJourneys(organisationId!),
    enabled: organisationId !== null,
  });

  const [action, setAction] = useState<JourneyAction | null>(null);

  const shown = (journeys.data ?? []).filter((journey) =>
    matchesJourneyFilter(journey, filter),
  );

  return (
    <>
      <PageHeader title='Journeys' />
      <PageBody className='flex flex-col gap-5'>
        <p className='text-base text-ink-muted'>
          Who is where in their journey at {organisation?.name ?? 'this organisation'}:
          dates and statuses only, never what was said.
        </p>

        {organisationId !== null && (
          <JourneysFeatureNotice organisationId={organisationId} />
        )}

        <JourneyPlanningTable
          journeys={shown}
          labels={JOURNEY_PLANNING_LABELS}
          locale={JOURNEY_LOCALE}
          rowHref={(journey) => `/journeys/organisations/${organisationId}/${journey.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          onStop={(journey) => setAction({ kind: 'stop', journey })}
          onDelete={(journey) => setAction({ kind: 'delete', journey })}
          renderReachability={(journey) => (
            <ManagerReachabilityBadge reachability={journey.manager_reachability} />
          )}
          loading={organisationId === null || journeys.isPending}
          error={journeys.isError ? 'Failed to load the journeys' : undefined}
          emptyState={
            <EmptyState
              title={filter === 'all' ? 'No journeys yet' : 'No journeys in this view'}
              description={
                filter === 'all'
                  ? 'A journey follows one employee and their manager through the steps of a journey type.'
                  : 'Choose another status to see the rest.'
              }
            />
          }
          toolbar={
            <>
              <FilterSelect<JourneyFilter>
                hideLabel
                label='Status'
                options={JOURNEY_FILTERS}
                value={filter}
                onValueChange={onFilterChange}
              />
              <Link to='/journeys/new' className={buttonVariants({ size: 'sm' })}>
                New journey
              </Link>
            </>
          }
        />
      </PageBody>

      {organisationId !== null && (
        <JourneyActionDialogs
          organisationId={organisationId}
          action={action}
          onClose={() => setAction(null)}
        />
      )}
    </>
  );
}
